import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Grid,
  Box,
  Paper,
  ThemeProvider,
  createTheme,
  Radio,
  RadioGroup,
  Chip,
  Divider,
  TextField,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteForever as DeleteForeverIcon,
  OtherHouses as HubIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Smartphone as SmartphoneIcon,
  LocalPhone as LocalPhoneIcon,
  LocationOn as LocationOnIcon,
  AccountTree as AccountTreeIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

// --- Theme Configuration (Identical aesthetic to OrderDetails) ---
const theme = createTheme({
  typography: {
    fontFamily: 'ProductSansLight, sans-serif',
    h3: { fontFamily: 'ProductSansBold, sans-serif', fontSize: 24, fontWeight: 600 },
    h5: { fontFamily: 'ProductSansBold, sans-serif', fontWeight: 700 },
    subtitle1: { fontSize: 14, color: '#64748b' },
    h6: { fontSize: 16, fontWeight: 600, color: '#1e293b' },
    subtitle2: { fontSize: 13, fontWeight: 600, color: '#475569' }
  },
  palette: {
    primary: { main: '#0ea5e9' },
    success: { main: '#10b981' }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }
      }
    }
  }
});

const MerchantDetails = ({
  reactTableData,
  onAdd,
  onEdit,
  onDelete,
  onAssignHub,
  onRefresh,
}) => {
  const rows = useMemo(() => reactTableData?.tableRowsData || [], [reactTableData]);

  const [selectedMerchantId, setSelectedMerchantId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusTab, setStatusTab] = useState('ALL'); // 'ALL' | 'ACTIVE' | 'INACTIVE'
  const [selectedHubFilter, setSelectedHubFilter] = useState('All Hubs');

  // Extract unique hub names from rows
  const uniqueHubs = useMemo(() => {
    const hubs = rows
      .map(r => r.hubName)
      .filter(Boolean);
    return Array.from(new Set(hubs));
  }, [rows]);

  // Counts for tabs
  const counts = useMemo(() => {
    const all = rows.length;
    const active = rows.filter(r => Number(r.merchantStatus) === 1).length;
    const inactive = all - active;
    return { all, active, inactive };
  }, [rows]);

  // Filtered merchants
  const filteredMerchants = useMemo(() => {
    return rows.filter(merchant => {
      // 1. Text Search matching name, code, contact name, contact mobile, landline, address, branch, or hub name
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q ||
        merchant.merchantName?.toLowerCase().includes(q) ||
        merchant.merchantCode?.toLowerCase().includes(q) ||
        merchant.merchantContactName?.toLowerCase().includes(q) ||
        merchant.merchantContactMobileNo?.toLowerCase().includes(q) ||
        merchant.merchantTelPhone?.toLowerCase().includes(q) ||
        merchant.merchantAddress?.toLowerCase().includes(q) ||
        merchant.merchantBranch?.toLowerCase().includes(q) ||
        merchant.hubName?.toLowerCase().includes(q);

      // 2. Status Tab Filter
      let matchesStatus = true;
      if (statusTab === 'ACTIVE') {
        matchesStatus = Number(merchant.merchantStatus) === 1;
      } else if (statusTab === 'INACTIVE') {
        matchesStatus = Number(merchant.merchantStatus) !== 1;
      }

      // 3. Hub Assignment Filter
      let matchesHub = true;
      if (selectedHubFilter === 'Assigned') {
        matchesHub = Boolean(merchant.hubId || merchant.secHubId || merchant.terHubId || merchant.hubName);
      } else if (selectedHubFilter === 'Unassigned') {
        matchesHub = !merchant.hubId && !merchant.secHubId && !merchant.terHubId && !merchant.hubName;
      } else if (selectedHubFilter !== 'All Hubs') {
        matchesHub = merchant.hubName === selectedHubFilter;
      }

      return matchesSearch && matchesStatus && matchesHub;
    });
  }, [rows, searchQuery, statusTab, selectedHubFilter]);

  // Default selection: select the first merchant if current selected is not valid
  useEffect(() => {
    if (filteredMerchants.length > 0) {
      if (!selectedMerchantId || !filteredMerchants.some(m => m.merchantId === selectedMerchantId)) {
        setSelectedMerchantId(filteredMerchants[0].merchantId);
      }
    } else {
      setSelectedMerchantId(null);
    }
  }, [filteredMerchants, selectedMerchantId]);

  const selectedMerchant = useMemo(() => {
    return rows.find(r => r.merchantId === selectedMerchantId) || null;
  }, [rows, selectedMerchantId]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusTab('ALL');
    setSelectedHubFilter('All Hubs');
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        {/* Top Header Bar */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
          <Stack>
            <Typography variant="h3">Merchant Details</Typography>
            <Typography variant="subtitle1">Manage merchants, contact details, and hub assignments in real-time.</Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onAdd}
              sx={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)', color: 'white', px: 2 }}
            >
              Add Merchant
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={onAssignHub}
              sx={{
                color: '#0284c7',
                borderColor: '#bae6fd',
                backgroundColor: '#ffffff',
                '&:hover': { backgroundColor: '#f0f9ff', borderColor: '#7dd3fc' },
                px: 2,
              }}
            >
              Assign to Hub
            </Button>
            <Tooltip title="Refresh Details">
              <IconButton
                onClick={onRefresh}
                sx={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  color: '#0284c7',
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: '#f1f5f9',
                  },
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                  height: '40px',
                  width: '40px',
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Two-Panel Layout */}
        <Grid container spacing={3}>
          {/* ── Left Panel (Selected Merchant Details) ── */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 4, minHeight: '740px', display: 'flex', flexDirection: 'column' }}>
              {selectedMerchant ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Merchant Identity Header */}
                  <Box sx={{ mb: 3, pb: 2.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            bgcolor: '#e0f2fe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0284c7'
                          }}
                        >
                          <StorefrontIcon sx={{ fontSize: 28 }} />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {selectedMerchant.merchantName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Merchant ID: #{selectedMerchant.merchantId}
                            {selectedMerchant.customerId ? ` • Customer ID: ${selectedMerchant.customerId}` : ''}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                        <Chip
                          label={selectedMerchant.merchantCode || 'No Code'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600, fontSize: '11px' }}
                        />
                        {Number(selectedMerchant.merchantStatus) === 1 ? (
                          <Chip
                            label="ACTIVE"
                            size="small"
                            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: '10px' }}
                          />
                        ) : (
                          <Chip
                            label="IN-ACTIVE"
                            size="small"
                            sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '10px' }}
                          />
                        )}
                        {selectedMerchant.merchantBranch && (
                          <Chip
                            label={`Branch: ${selectedMerchant.merchantBranch}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '11px', color: '#475569' }}
                          />
                        )}
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => onEdit(selectedMerchant)}
                        sx={{ px: 1.5, py: 0.5, fontSize: '12px', minWidth: 'auto', height: '34px' }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteForeverIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => onDelete(selectedMerchant)}
                        sx={{ px: 1.5, py: 0.5, fontSize: '12px', minWidth: 'auto', height: '34px' }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </Box>

                  {/* Hub Assignment Banner */}
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      bgcolor: '#f0f9ff',
                      borderRadius: '12px',
                      border: '1px solid #bae6fd',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1.5
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ m: 0, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                        <HubIcon
                          sx={{
                            fontSize: 20,
                            color: (selectedMerchant.hubId || selectedMerchant.secHubId || selectedMerchant.terHubId || selectedMerchant.hubName) ? '#10b981' : '#64748b'
                          }}
                        />
                        Hub Assignment:
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={selectedMerchant.hubName ? `Primary: ${selectedMerchant.hubName}` : 'No Primary Hub Assigned'}
                          sx={{
                            bgcolor: selectedMerchant.hubName ? '#e0f2fe' : '#f1f5f9',
                            color: selectedMerchant.hubName ? '#0369a1' : '#64748b',
                            fontWeight: 600,
                            fontSize: '11px'
                          }}
                        />
                        {selectedMerchant.secHubName && (
                          <Chip
                            size="small"
                            label={`Secondary: ${selectedMerchant.secHubName}`}
                            sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: '11px' }}
                          />
                        )}
                        {selectedMerchant.terHubName && (
                          <Chip
                            size="small"
                            label={`Tertiary: ${selectedMerchant.terHubName}`}
                            sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', fontSize: '11px' }}
                          />
                        )}
                      </Stack>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AssignmentIcon sx={{ fontSize: '15px !important' }} />}
                      onClick={() => onAssignHub(selectedMerchant)}
                      sx={{
                        fontSize: '11px',
                        textTransform: 'none',
                        py: 0.5,
                        bgcolor: '#0284c7',
                        color: '#ffffff',
                        '&:hover': { bgcolor: '#0369a1' }
                      }}
                    >
                      Manage Hub
                    </Button>
                  </Box>

                  {/* Two Columns: Contact Details & Location Details */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2.5, bgcolor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ color: '#0284c7', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, mb: 2 }}>
                          <PersonIcon sx={{ fontSize: 18 }} /> Contact Information
                        </Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {selectedMerchant.merchantContactName || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Contact Mobile</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SmartphoneIcon sx={{ fontSize: 16, color: '#64748b' }} />
                              {selectedMerchant.merchantContactMobileNo || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Landline Phone</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocalPhoneIcon sx={{ fontSize: 16, color: '#64748b' }} />
                              {selectedMerchant.merchantTelPhone || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Branch</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccountTreeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                              {selectedMerchant.merchantBranch || '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2.5, bgcolor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, mb: 2 }}>
                          <LocationOnIcon sx={{ fontSize: 18 }} /> Address & Location
                        </Typography>
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Merchant Address</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.5, wordBreak: 'break-word' }}>
                              {selectedMerchant.merchantAddress || 'No address specified'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">GRL Code</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {selectedMerchant.merchantGrl || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Geo-Coordinates</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {selectedMerchant.latitude && selectedMerchant.longitude
                                ? `${selectedMerchant.latitude}, ${selectedMerchant.longitude}`
                                : '—'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Footer Info */}
                  <Box sx={{ mt: 'auto', pt: 3, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing profile details for {selectedMerchant.merchantName}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Status: {Number(selectedMerchant.merchantStatus) === 1 ? 'Active' : 'In-Active'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ m: 'auto', textAlign: 'center', py: 8 }}>
                  <StorefrontIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" color="text.disabled">
                    {filteredMerchants.length === 0 ? 'No merchants found matching filters' : 'Select a merchant from the list to view details'}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ── Right Panel (Searchable / Filterable Merchant List) ── */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, height: '740px', display: 'flex', flexDirection: 'column' }}>
              {/* Status Filter Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={statusTab}
                  onChange={(e, val) => setStatusTab(val)}
                  variant="fullWidth"
                  textColor="primary"
                  indicatorColor="primary"
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      textTransform: 'none',
                      color: '#64748b',
                      '&.Mui-selected': {
                        color: '#0ea5e9',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#0ea5e9',
                      height: '3px',
                      borderRadius: '3px'
                    }
                  }}
                >
                  <Tab label={`ALL (${counts.all})`} value="ALL" />
                  <Tab label={`ACTIVE (${counts.active})`} value="ACTIVE" />
                  <Tab label={`IN-ACTIVE (${counts.inactive})`} value="INACTIVE" />
                </Tabs>
              </Box>

              {/* Filters & Search Control */}
              <Box mb={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">Filters</Typography>
                    {(searchQuery || selectedHubFilter !== 'All Hubs' || statusTab !== 'ALL') && (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={handleClearFilters}
                        sx={{ textTransform: 'none', py: 0, px: 1, fontSize: '11px', minWidth: 0 }}
                      >
                        Clear
                      </Button>
                    )}
                  </Stack>
                  <TextField
                    size="small"
                    placeholder="Search by name, code, contact..."
                    variant="standard"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 1, color: 'text.disabled' }} /> }}
                  />
                </Stack>

                {/* Hub Assignment Filter Dropdown */}
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedHubFilter}
                    onChange={e => setSelectedHubFilter(e.target.value)}
                    SelectProps={{ native: true }}
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffffff'
                      }
                    }}
                  >
                    <option value="All Hubs">All Hubs / Assignment</option>
                    <option value="Assigned">Assigned to Hub</option>
                    <option value="Unassigned">Unassigned (No Hub)</option>
                    {uniqueHubs.map(hub => (
                      <option key={hub} value={hub}>{`Hub: ${hub}`}</option>
                    ))}
                  </TextField>
                </Stack>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Header List Count */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" px={1} mb={1}>
                <Typography variant="subtitle2">Merchants ({filteredMerchants.length})</Typography>
              </Stack>

              {/* Scrollable List of Merchant Cards */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
                {filteredMerchants.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No merchants match your criteria.
                    </Typography>
                  </Box>
                ) : (
                  <RadioGroup
                    value={selectedMerchantId}
                    onChange={e => setSelectedMerchantId(Number(e.target.value))}
                  >
                    {filteredMerchants.map(merchant => {
                      const isSelected = selectedMerchantId === merchant.merchantId;
                      return (
                        <Box
                          key={merchant.merchantId}
                          onClick={() => setSelectedMerchantId(merchant.merchantId)}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            borderRadius: '12px',
                            border: '1px solid',
                            borderColor: isSelected ? '#0ea5e9' : '#e2e8f0',
                            bgcolor: isSelected ? '#f0f9ff' : '#ffffff',
                            boxShadow: isSelected ? '0 2px 6px rgba(14, 165, 233, 0.15)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': {
                              bgcolor: isSelected ? '#f0f9ff' : '#f8fafc',
                              borderColor: '#93c5fd'
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <Radio
                              size="small"
                              checked={isSelected}
                              value={merchant.merchantId}
                              sx={{ p: 0.5, mt: 0.2 }}
                            />
                            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                              {/* Row 1: Merchant Name + Code + Status */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }} noWrap>
                                    {merchant.merchantName}
                                  </Typography>
                                  {merchant.merchantCode && (
                                    <Chip
                                      label={merchant.merchantCode}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '10px', fontWeight: 600, color: '#0284c7', borderColor: '#bae6fd' }}
                                    />
                                  )}
                                </Box>
                                {Number(merchant.merchantStatus) === 1 ? (
                                  <Chip
                                    label="ACTIVE"
                                    size="small"
                                    sx={{ height: 18, fontSize: '9px', bgcolor: '#dcfce7', color: '#166534', fontWeight: 700 }}
                                  />
                                ) : (
                                  <Chip
                                    label="IN-ACTIVE"
                                    size="small"
                                    sx={{ height: 18, fontSize: '9px', bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 700 }}
                                  />
                                )}
                              </Box>

                              {/* Row 2: Contact Person & Mobile */}
                              <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }} noWrap>
                                <PersonIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                {merchant.merchantContactName || 'No contact'}
                                {merchant.merchantContactMobileNo && (
                                  <>
                                    <span style={{ color: '#cbd5e1' }}>•</span>
                                    <SmartphoneIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                    {merchant.merchantContactMobileNo}
                                  </>
                                )}
                              </Typography>

                              {/* Row 3: Hub Name & Branch */}
                              <Stack direction="row" spacing={1} alignItems="center" mt={0.75} flexWrap="wrap">
                                {merchant.hubName ? (
                                  <Chip
                                    icon={<HubIcon sx={{ fontSize: '13px !important', color: '#059669' }} />}
                                    label={merchant.hubName}
                                    size="small"
                                    sx={{ height: 20, fontSize: '10px', bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 600 }}
                                  />
                                ) : (
                                  <Chip
                                    label="No Hub"
                                    size="small"
                                    sx={{ height: 20, fontSize: '10px', bgcolor: '#f1f5f9', color: '#94a3b8' }}
                                  />
                                )}
                                {merchant.merchantBranch && (
                                  <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '11px' }}>
                                    {merchant.merchantBranch}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })}
                  </RadioGroup>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

MerchantDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.object),
  }),
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAssignHub: PropTypes.func,
  onRefresh: PropTypes.func,
};

MerchantDetails.defaultProps = {
  reactTableData: { tableRowsData: [] },
  onAdd: () => {},
  onEdit: () => {},
  onDelete: () => {},
  onAssignHub: () => {},
  onRefresh: () => {},
};

export default MerchantDetails;
